// src/components/MfaEnroll.js
import React, { useState, useEffect } from 'react';
import { supabase } from 'services/supabaseClient';
import QRCode from 'qrcode';

// Quick base32 validator
const isValidBase32 = (str) => /^[A-Z2-7]+=*$/.test(str);

export default function MfaEnroll({ onEnrolled, requiredMode = false }) {
  const [factorId, setFactorId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [hasMfa, setHasMfa] = useState(false);

  // Check existing MFA factors on mount
  useEffect(() => {
    const checkFactors = async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) {
        setError(error.message);
        return;
      }
      const totpFactor = data?.totp?.[0];
      if (totpFactor) {
        setHasMfa(true);
        setFactorId(totpFactor.id);
        setStatus('MFA is already enabled for your account.');
      }
    };
    checkFactors();
  }, []);

  // Step 1: Enroll TOTP
  const handleEnroll = async () => {
    setError('');
    setStatus('');
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (error) {
      setError(error.message);
      return;
    }

    setFactorId(data.id);

    if (data.totp?.uri) {
      try {
        const qrDataUrl = await QRCode.toDataURL(data.totp.uri);
        setQrCode(qrDataUrl);
      } catch (err) {
        setError('Error generating QR code: ' + err.message);
      }
    }

    if (data.totp?.secret && isValidBase32(data.totp.secret)) {
      setSecret(data.totp.secret);
    }

    setStatus(
      'Scan the QR code with your Authenticator app, then enter the 6-digit code below.'
    );
  };

  // Step 2: Verify the code
  const handleVerify = async () => {
    setError('');
    setStatus('');

    const { data: challenge, error: challengeError } =
      await supabase.auth.mfa.challenge({ factorId });

    if (challengeError) {
      setError('Error creating challenge: ' + challengeError.message);
      return;
    }

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code: otpCode.trim(),
    });

    if (verifyError) {
      setError('Invalid code. Please try again.');
      return;
    }

    await supabase.from('profiles').update({ mfa_enabled: true }).eq('id', (await supabase.auth.getUser()).data?.user?.id);

    setHasMfa(true);
    setQrCode(null);
    setSecret(null);
    setOtpCode('');
    setStatus('MFA setup complete. Two-factor authentication is now required at login.');
    if (typeof onEnrolled === 'function') onEnrolled();
  };

  // Allow user to re-enroll
  const handleReEnroll = async () => {
    if (!factorId) return;
    setError('');
    setStatus('');

    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) {
      setError('Error removing existing factor: ' + error.message);
      return;
    }

    await supabase.from('profiles').update({ mfa_enabled: false }).eq('id', (await supabase.auth.getUser()).data?.user?.id);

    setHasMfa(false);
    setFactorId(null);
    setQrCode(null);
    setSecret(null);
    setOtpCode('');
    setStatus('Previous MFA factor removed. You may now enroll again.');
  };

  // Allow user to fully disable MFA
  const handleUnenroll = async () => {
    if (!factorId) return;
    setError('');
    setStatus('');

    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) {
      setError('Error disabling MFA: ' + error.message);
      return;
    }

    setHasMfa(false);
    setFactorId(null);
    setQrCode(null);
    setSecret(null);
    setOtpCode('');
    setStatus('MFA has been disabled for your account.');
  };

  return (
    <div className="space-y-4">
      {!hasMfa && !factorId && (
        <button onClick={handleEnroll} className="btn btn-primary">
          Enable 2FA
        </button>
      )}

      {hasMfa && !qrCode && (
        <div className="space-y-4">
          <div className="text-sm text-muted space-y-1">
            <p>MFA is already enabled for your account.</p>
            <p>
              Click <strong>Re-enroll</strong> if you have changed phones or need
              to set up a new authenticator app.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReEnroll}
              className="btn btn-ghost border border-app"
            >
              Re-enroll 2FA
            </button>
            {!requiredMode && (
              <button
                onClick={handleUnenroll}
                className="btn btn-ghost border border-red-400 text-red-600"
              >
                Disable 2FA
              </button>
            )}
          </div>
        </div>
      )}

      {factorId && qrCode && (
        <div className="space-y-4">
          <img src={qrCode} alt="TOTP QR Code" className="mx-auto" />

          {secret && (
            <p className="text-xs text-muted text-center">
              Or enter this secret manually: <code>{secret}</code>
            </p>
          )}

          <p className="text-sm text-muted text-center">
            After scanning, enter the 6-digit code generated by your Authenticator app:
          </p>

          <input
            type="text"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
            className="w-full px-4 py-2 rounded-md border border-app input-app"
            placeholder="Enter 6-digit code"
          />

          <button onClick={handleVerify} className="btn btn-primary w-full">
            Verify Code
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {status && !error && !hasMfa && (
        <div className="rounded-md border border-green-500/40 bg-green-500/10 p-3 text-sm text-green-600">
          {status}
        </div>
      )}
    </div>
  );
}
